/**
 * =============================================================================
 * IDS CRM Web - Maintenance Task Detail Page
 * =============================================================================
 *
 * PURPOSE:
 * Displays scheduled maintenance task details (Quarterly Inspections, Annual Services)
 * with completion form, reschedule history, and sign-off attachments.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import BackToList from '../components/BackToList';
import { useAuth, API_URL } from '../context/AuthContext';
import EngineerSelect from '../components/EngineerSelect';
import { filesToAttachmentPayload, savedAttachmentsOnly } from '../utils/attachments';
import TemplateCheckFormRenderer, { validateAnswers } from '../components/TemplateCheckFormRenderer';
import { checkCategoryForAssetType } from '../utils/checkFormTargeting';
import useTemplateCheckForm from '../hooks/useTemplateCheckForm';
import { formOwnsCompletion, completionTicked } from '../utils/formCompletion';
import { useToast } from '../components/Toast';
import { formatDateKey } from '../utils/dates';
import { formatDuration } from '../utils/duration';
import ReviewNotes from '../components/ReviewNotes';
import './MaintenanceTaskDetail.css';
import OpsBoardServiceStatus from '../components/OpsBoardServiceStatus';

// A job is opened from Scheduling and from Jobs & Tickets: its "←" link goes
// back to the list it was opened from, as that list was left.
const JOB_LISTS = { '/tasks': 'Scheduling', '/tickets': 'Jobs & Tickets' };

// maintenance_reschedule_log.change_type: the scheduler writes 'manual' from
// the Reschedule button and 'automatic_from_completion' when signing off a
// robot-plan visit moves the later ones.
const RESCHEDULE_CHANGE_TYPES = {
  manual: { label: 'Manual reschedule', className: 'manual' },
  automatic_from_completion: { label: 'Automatic after sign-off', className: 'auto' }
};

const changeTypeOf = (entry) => RESCHEDULE_CHANGE_TYPES[entry.change_type] || { label: 'Reschedule', className: '' };

function MaintenanceTaskDetail() {
  const { id } = useParams();
  const { authFetch, user } = useAuth();
  const { toastElement, showToast } = useToast();

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [engineers, setEngineers] = useState([]);
  const [faultCategories, setFaultCategories] = useState([]);

  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const [completeForm, setCompleteForm] = useState({
    completed_at: '',
    engineer_id: '',
    notes: '',
    outcome: 'completed',
    attachments: [],
    follow_up_required: false,
    create_follow_up_ticket: false,
    follow_up_subject: '',
    follow_up_description: '',
    follow_up_priority: 'normal',
    follow_up_fault_category: '',
    asset_health: ''
  });

  const [rescheduleForm, setRescheduleForm] = useState({
    new_due_date: '',
    reason: ''
  });

  const checkCategory = task?.asset_id ? checkCategoryForAssetType(task?.robot_type) : null;

  // Check form template targeting params (category from the asset type,
  // workLogType is the scheduled_maintenance context).
  const maintenanceResolveParams = {
    category: checkCategory,
    workLogType: 'scheduled_maintenance',
    assetType: task?.robot_type
  };
  if (task?.task_type) {
    maintenanceResolveParams.maintenanceType = task.task_type;
  }
  // Quarterly tasks carry a contract year too - always send it so
  // year-targeted templates can never leak onto the wrong visit.
  if (task?.cycle_year) {
    maintenanceResolveParams.serviceYear = String(task.cycle_year);
  }

  // Check form template state - resolved by Check Form Template targeting
  // each time the sign-off modal opens.
  const templateCheck = useTemplateCheckForm({
    authFetch,
    enabled: Boolean(showCompleteModal && checkCategory),
    resolveParams: maintenanceResolveParams,
    conflictMessage: (errorData) => {
      const names = (errorData.conflicting_templates || []).map(t => t.name).join(', ');
      return `Multiple check templates target scheduled maintenance for this asset (${names}). An admin needs to fix the targeting in Settings before this task can be signed off.`;
    },
    onError: (message) => showToast(message, 'error'),
    logLabel: 'Maintenance Check'
  });
  const {
    template: checkTemplate,
    loading: checkTemplateLoading,
    failed: checkTemplateFailed,
    answers: checkAnswers,
    setAnswers: setCheckAnswers,
    attachments: checkAttachments,
    addAttachments: addCheckAttachments,
    removeAttachment: removeCheckAttachment,
    resolveWarning: checkResolveWarning,
    completed: checkCompleted,
    setCompleted: setCheckCompleted
  } = templateCheck;

  const useCheckTemplate = Boolean(checkCategory && checkTemplate && !checkTemplateLoading && !checkTemplateFailed);
  const showCheckWarning = Boolean(checkCategory && checkResolveWarning && !checkTemplate && !checkTemplateLoading);

  // A form with its own completion tick owns completion, as on tickets: the
  // fixed box is left out and the form's tick decides.
  const formTick = useCheckTemplate && formOwnsCompletion(checkTemplate.schema_json);
  useEffect(() => {
    if (!formTick) return;
    const ticked = completionTicked(checkAnswers);
    if (ticked !== checkCompleted) setCheckCompleted(ticked);
  }, [formTick, checkAnswers, checkCompleted, setCheckCompleted]);

  const fetchTask = useCallback(async () => {
    try {
      const response = await authFetch(`/api/v1/maintenance/tasks/${id}`);
      if (!response.ok) throw new Error('Task not found');
      const data = await response.json();
      setTask(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, authFetch]);

  useEffect(() => {
    fetchTask();
  }, [fetchTask]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const [engineersRes, categoriesRes] = await Promise.all([
          authFetch('/api/v1/engineers?active=true'),
          authFetch('/api/v1/support/fault-categories')
        ]);
        if (engineersRes.ok) {
          const data = await engineersRes.json();
          setEngineers(data.data || []);
        }
        if (categoriesRes.ok) {
          const data = await categoriesRes.json();
          setFaultCategories(data.data || []);
        }
      } catch (err) {
        console.error('Failed to load config:', err);
      }
    };
    fetchConfig();
  }, [authFetch]);

  useEffect(() => {
    if (user && !completeForm.engineer_id) {
      setCompleteForm(prev => ({ ...prev, engineer_id: user.userId || '' }));
    }
  }, [user, completeForm.engineer_id]);

  // Due dates are calendar days (DATE columns): read them as days so they
  // match the schedule in every time zone. Timestamps use formatDateTime.
  const formatDate = (dateStr) => formatDateKey(dateStr, {
    day: 'numeric', month: 'short', year: 'numeric'
  });

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getTaskTypeName = (task) => {
    if (!task) return '';
    if (task.task_type === 'annual_service') return 'Annual Service';
    return `Quarterly Inspection ${task.health_check_number || ''}`;
  };

  const handleCompleteSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // A fully answered, signed-off check form is required for completing
      // outcomes; partial/unable outcomes save whatever was answered so far.
      const isCompletingOutcome = ['completed', 'completed_with_issues'].includes(completeForm.outcome);
      if (showCheckWarning) {
        throw new Error('Check template targeting is ambiguous - an admin must fix it in Settings before this task can be signed off.');
      }
      if (useCheckTemplate && isCompletingOutcome) {
        const validation = validateAnswers(checkTemplate.schema_json, checkAnswers, { requireComplete: true, attachments: checkAttachments });
        if (!validation.valid) {
          const firstError = validation.errors[0];
          throw new Error(`${checkTemplate.name || 'Check'}: ${firstError.fieldLabel ? `${firstError.fieldLabel} - ` : ''}${firstError.message}`);
        }
        if (!checkCompleted) {
          throw new Error(`Please confirm the ${checkTemplate.name || 'check'} is complete before signing off.`);
        }
      }

      const payload = {
        completed_at: completeForm.completed_at || new Date().toISOString(),
        engineer_id: completeForm.engineer_id,
        notes: completeForm.notes,
        outcome: completeForm.outcome,
        attachments: completeForm.attachments,
        follow_up_required: completeForm.follow_up_required,
        create_follow_up_ticket: completeForm.create_follow_up_ticket
      };
      if (completeForm.asset_health) {
        payload.asset_health = completeForm.asset_health;
      }

      const hasCheckContent = Object.keys(checkAnswers).length > 0 || Object.keys(checkAttachments).length > 0;
      if (useCheckTemplate && (isCompletingOutcome || hasCheckContent)) {
        payload.template_checks = [templateCheck.buildTemplateCheckEntry(task?.asset_id)];
      }

      if (completeForm.create_follow_up_ticket) {
        payload.follow_up_subject = completeForm.follow_up_subject;
        payload.follow_up_description = completeForm.follow_up_description;
        payload.follow_up_priority = completeForm.follow_up_priority;
        payload.follow_up_fault_category = completeForm.follow_up_fault_category;
      }

      const response = await authFetch(`/api/v1/maintenance/tasks/${id}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || 'Failed to complete task');
      }

      setShowCompleteModal(false);
      fetchTask();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRescheduleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await authFetch(`/api/v1/maintenance/tasks/${id}/reschedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // new_due_date is the field the API validates; posting new_date got
        // every manual reschedule a 400 "Failed to reschedule task".
        body: JSON.stringify({
          new_due_date: rescheduleForm.new_due_date,
          reason: rescheduleForm.reason
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || 'Failed to reschedule task');
      }

      setShowRescheduleModal(false);
      setRescheduleForm({ new_due_date: '', reason: '' });
      fetchTask();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleFileChange = async (e) => {
    const picker = e.target;
    try {
      const attachments = await filesToAttachmentPayload(picker.files);
      setCompleteForm(prev => ({ ...prev, attachments }));
    } catch (err) {
      // Refused files are not attached: the picker is emptied so it cannot go on
      // showing files that will not be saved.
      picker.value = '';
      setCompleteForm(prev => ({ ...prev, attachments: savedAttachmentsOnly(prev.attachments) }));
      showToast(err.message, 'error');
    }
  };

  if (loading) {
    return <div className="loading-page">Loading maintenance task...</div>;
  }

  if (error) {
    return (
      <div className="error-page">
        <h2>Error</h2>
        <p>{error}</p>
        <BackToList to="/tasks" also={['/tickets']} labels={JOB_LISTS} className="btn-secondary">← Scheduling</BackToList>
      </div>
    );
  }

  const isOverdue = task.status === 'scheduled' && task.is_overdue;
  const isCompleted = task.status === 'completed';

  return (
    <div className="maintenance-detail">
      {toastElement}
      {/* Header Card */}
      <div className="detail-header">
        <div className="header-left">
          <div className="header-info">
            <Link to={`/assets/${task.asset_id}`} className="back-link">← Asset</Link>
            <div className="header-row-1">
              <h1>{task.task_ref}</h1>
              <span className={`type-chip ${task.task_type}`}>
                {getTaskTypeName(task)}
              </span>
              <span className={`status-chip ${task.status}${isOverdue ? ' overdue' : ''}`}>
                {isOverdue ? `Overdue (${task.calculated_days_overdue}d)` : task.status}
              </span>
            </div>
            <div className="header-row-2">
              <span className="meta-chip">Year {task.cycle_year}</span>
              {task.task_type === 'health_check' && (
                <span className="meta-chip">Q{task.health_check_number}</span>
              )}
            </div>
            <div className="header-row-3">
              <span className="meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4z"/>
                </svg>
                <Link to={`/customers/${task.customer_id}`} className="customer-link">{task.customer_name}</Link>
              </span>
              <span className="meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                  <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
                </svg>
                <Link to={`/assets/${task.asset_id}`} className="asset-link">{task.asset_ref}</Link>
              </span>
              {task.site_name && (
                <span className="meta-item">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="10" r="3"/>
                    <path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 10-16 0c0 3 2.7 7 8 11.7z"/>
                  </svg>
                  {task.site_name}
                </span>
              )}
            </div>
            {/* On the Ops Planning board, or add it (Gareth 2026-10-21). */}
            <OpsBoardServiceStatus type="maintenance_task" id={id} />
          </div>
        </div>
        <div className="header-actions">
          {task.status === 'scheduled' && (
            <>
              <button className="btn-action" onClick={() => setShowRescheduleModal(true)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                Reschedule
              </button>
              <button className="btn-action primary" onClick={() => setShowCompleteModal(true)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Sign Off
              </button>
            </>
          )}
        </div>
      </div>

      {/* Overdue Alert */}
      {isOverdue && (
        <div className="alert-bar warning">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <span className="alert-message">
            This maintenance task is {task.calculated_days_overdue} days overdue. Original due date was {formatDate(task.current_due_date)}.
          </span>
        </div>
      )}

      {/* Tabs */}
      <div className="detail-tabs">
        <button className={activeTab === 'details' ? 'tab active' : 'tab'} onClick={() => setActiveTab('details')}>
          Details
        </button>
        <button className={activeTab === 'history' ? 'tab active' : 'tab'} onClick={() => setActiveTab('history')}>
          Reschedule History ({task.reschedule_history?.length || 0})
        </button>
      </div>

      {/* Tab Content */}
      <div className="detail-content">
        {activeTab === 'details' && (
          <div className="details-grid">
            {/* Schedule Card */}
            <div className="detail-card">
              <div className="card-header">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                <h3>SCHEDULE</h3>
              </div>
              <div className="card-body">
                <div className="info-row">
                  <span className="info-label">Original Due</span>
                  <span className="info-value">{formatDate(task.original_scheduled_date)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Current Due</span>
                  <span className={`info-value ${isOverdue ? 'breached' : ''}`}>
                    {formatDate(task.current_due_date)}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Contract Year</span>
                  <span className="info-value">Year {task.cycle_year} of 5</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Est. Duration</span>
                  <span className="info-value">{formatDuration(task.estimated_duration_mins)}</span>
                </div>
              </div>
            </div>

            {/* Asset Card */}
            <div className="detail-card">
              <div className="card-header">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                  <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
                </svg>
                <h3>ASSET</h3>
              </div>
              <div className="card-body">
                <div className="info-row">
                  <span className="info-label">Asset Ref</span>
                  <span className="info-value highlight">
                    <Link to={`/assets/${task.asset_id}`}>{task.asset_ref}</Link>
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Serial</span>
                  <span className="info-value">{task.asset_serial || '-'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Type</span>
                  <span className="info-value">{task.robot_type?.replace(/_/g, ' ') || '-'}</span>
                </div>
              </div>
            </div>

            {/* Assignment Card */}
            <div className="detail-card">
              <div className="card-header">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                </svg>
                <h3>ASSIGNMENT</h3>
              </div>
              <div className="card-body">
                <div className="info-row">
                  <span className="info-label">Assigned To</span>
                  <span className="info-value">{task.assigned_engineer_names || 'Unassigned'}</span>
                </div>
                {task.site_name && (
                  <div className="info-row">
                    <span className="info-label">Site</span>
                    <span className="info-value">{task.site_name}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Completion Card (if completed) */}
            {isCompleted && (
              <div className="detail-card full-width">
                <div className="card-header">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <h3>COMPLETION</h3>
                </div>
                <div className="card-body">
                  <div className="completion-grid">
                    <div className="info-row">
                      <span className="info-label">Completed</span>
                      <span className="info-value">{formatDateTime(task.completed_at || task.completed_date)}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Completed By</span>
                      <span className="info-value">{task.completed_by_name || '-'}</span>
                    </div>
                    {task.days_overdue > 0 && (
                      <div className="info-row">
                        <span className="info-label">Days Late</span>
                        <span className="info-value breached">{task.days_overdue} days</span>
                      </div>
                    )}
                    {task.completion_outcome && (
                      <div className="info-row">
                        <span className="info-label">Outcome</span>
                        <span className="info-value">{task.completion_outcome.replace(/_/g, ' ')}</span>
                      </div>
                    )}
                  </div>
                  {task.notes && (
                    <div className="notes-section">
                      <h4>Work Notes</h4>
                      <p>{task.notes}</p>
                    </div>
                  )}
                  {task.follow_up_required && (
                    <div className="follow-up-notice">
                      Follow-up required for this maintenance task
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Check Form Card (if completed with a submitted check form) */}
            {isCompleted && task.template_checks && task.template_checks.length > 0 && (
              <div className="detail-card full-width">
                <div className="card-header">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 11l3 3L22 4"/>
                    <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
                  </svg>
                  <h3>CHECK FORM</h3>
                </div>
                <div className="card-body">
                  {task.template_checks.map(check => (
                    <div key={check.check_id} className="completed-check">
                      <div className="completed-check-meta">
                        <strong>{check.template_snapshot?.name || 'Check form'}</strong>
                        <span>
                          {check.check_completed ? 'Completed' : 'Partially completed'}
                          {' · '}{check.submitted_by_name || '-'}
                          {' · '}{formatDateTime(check.submitted_at)}
                        </span>
                      </div>
                      <TemplateCheckFormRenderer
                        schema={check.template_snapshot}
                        answers={check.template_answers || {}}
                        stepAttachments={(Array.isArray(check.attachments) ? check.attachments : []).reduce((byStep, item) => {
                          if (item && item.step_key) byStep[item.step_key] = [...(byStep[item.step_key] || []), item];
                          return byStep;
                        }, {})}
                        onAnswersChange={() => {}}
                        disabled
                      />
                      {Array.isArray(check.attachments) && check.attachments.length > 0 && (
                        <div className="attachments-grid">
                          {check.attachments.map((attachment, index) => (
                            <a
                              key={attachment.attachment_id || index}
                              href={`${API_URL}${attachment.url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="attachment-item"
                            >
                              {attachment.type && attachment.type.startsWith('image/') ? (
                                <img src={`${API_URL}${attachment.url}`} alt={attachment.name} className="attachment-thumbnail" />
                              ) : (
                                <div className="attachment-icon">
                                  {attachment.type === 'application/pdf' ? 'PDF' : 'FILE'}
                                </div>
                              )}
                              <span className="attachment-name">{attachment.name}</span>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Attachments Card (if completed with attachments) */}
            {isCompleted && task.completion_attachments && task.completion_attachments.length > 0 && (
              <div className="detail-card full-width">
                <div className="card-header">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
                  </svg>
                  <h3>SIGN-OFF ATTACHMENTS</h3>
                </div>
                <div className="card-body">
                  <div className="attachments-grid">
                    {task.completion_attachments.map((attachment, index) => (
                      <a
                        key={attachment.attachment_id || index}
                        href={`${API_URL}${attachment.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="attachment-item"
                      >
                        {attachment.type && attachment.type.startsWith('image/') ? (
                          <img src={`${API_URL}${attachment.url}`} alt={attachment.name} className="attachment-thumbnail" />
                        ) : (
                          <div className="attachment-icon">
                            {attachment.type === 'application/pdf' ? 'PDF' : 'FILE'}
                          </div>
                        )}
                        <span className="attachment-name">{attachment.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="review-notes-section">
            {/* Notes from the Jobs & Tickets board review (2026-10-20). */}
            <ReviewNotes type="maintenance_task" id={id} />
          </div>
        )}

        {activeTab === 'history' && (
          <div className="history-section">
            {!task.reschedule_history || task.reschedule_history.length === 0 ? (
              <div className="empty-message">No reschedule history</div>
            ) : (
              <div className="history-list" role="list">
                {task.reschedule_history.map((entry, index) => (
                  <div key={entry.log_id || index} className="history-item" role="listitem">
                    <div className="history-header">
                      <span className={`history-type ${changeTypeOf(entry).className}`}>
                        {changeTypeOf(entry).label}
                      </span>
                      <span className="history-date">{formatDateTime(entry.changed_at)}</span>
                    </div>
                    <div className="history-body">
                      <div className="history-dates">
                        <span>{formatDate(entry.previous_due_date)}</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="5" y1="12" x2="19" y2="12"/>
                          <polyline points="12 5 19 12 12 19"/>
                        </svg>
                        <span>{formatDate(entry.new_due_date)}</span>
                        <span className={`days-diff ${entry.days_difference > 0 ? 'later' : 'earlier'}`}>
                          ({entry.days_difference > 0 ? '+' : ''}{entry.days_difference} days)
                        </span>
                      </div>
                      {entry.reason && <p className="history-reason">{entry.reason}</p>}
                      <span className="history-by">by {entry.changed_by_name || 'System'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Complete Modal */}
      {showCompleteModal && (
        <div className="modal-overlay">
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Sign Off Maintenance</h2>
              <button className="modal-close" onClick={() => setShowCompleteModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleCompleteSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Completion Date/Time *</label>
                    <input
                      type="datetime-local"
                      value={completeForm.completed_at}
                      onChange={e => setCompleteForm(prev => ({ ...prev, completed_at: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Completed By *</label>
                    <EngineerSelect
                      value={completeForm.engineer_id}
                      onChange={e => setCompleteForm(prev => ({ ...prev, engineer_id: e.target.value }))}
                      required
                      engineers={engineers}
                      valueKey="engineer_id"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Outcome *</label>
                  <select
                    value={completeForm.outcome}
                    onChange={e => setCompleteForm(prev => ({ ...prev, outcome: e.target.value }))}
                    required
                  >
                    <option value="completed">Completed Successfully</option>
                    <option value="completed_with_issues">Completed with Issues</option>
                    <option value="partial">Partial Completion</option>
                    <option value="unable_to_complete">Unable to Complete</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Asset Condition After Visit</label>
                  <select
                    value={completeForm.asset_health}
                    onChange={e => setCompleteForm(prev => ({ ...prev, asset_health: e.target.value }))}
                  >
                    <option value="">Leave unchanged</option>
                    <option value="green">Green - healthy</option>
                    <option value="amber">Amber - keep an eye on it</option>
                    <option value="red">Red - needs attention</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Work Notes *</label>
                  <textarea
                    value={completeForm.notes}
                    onChange={e => setCompleteForm(prev => ({ ...prev, notes: e.target.value }))}
                    rows={4}
                    placeholder="Describe work performed, findings, any issues..."
                    required
                  />
                </div>

                {showCheckWarning && (
                  <div className="modal-error">{checkResolveWarning}</div>
                )}

                {checkTemplateLoading && (
                  <span className="field-hint">Loading check form...</span>
                )}

                {useCheckTemplate && (
                  <div className="template-check-section">
                    <div className="template-check-header">
                      <h3>{checkTemplate.name || checkTemplate.schema_json?.name || 'Check Form'}</h3>
                      <p>Required for this asset before the task can be signed off as completed.</p>
                    </div>
                    <TemplateCheckFormRenderer
                      schema={checkTemplate.schema_json}
                      answers={checkAnswers}
                      onAnswersChange={setCheckAnswers}
                      stepAttachments={checkAttachments}
                      onAddPhotos={addCheckAttachments}
                      onRemovePhoto={removeCheckAttachment}
                    />
                    {!formTick && (
                      <label className="template-check-complete">
                        <input
                          type="checkbox"
                          checked={checkCompleted}
                          onChange={e => setCheckCompleted(e.target.checked)}
                        />
                        Check complete - all items answered and accurate
                      </label>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label>Sign-off Attachments *</label>
                  <input type="file" multiple onChange={handleFileChange} accept="image/*,.pdf" />
                  {completeForm.attachments.length > 0 && (
                    <span className="file-count">{completeForm.attachments.length} file(s) selected</span>
                  )}
                  <span className="field-hint">Photos of completed work, signed paperwork, etc.</span>
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={completeForm.follow_up_required}
                      onChange={e => setCompleteForm(prev => ({ ...prev, follow_up_required: e.target.checked }))}
                    />
                    Follow-up required
                  </label>
                </div>

                {completeForm.follow_up_required && (
                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={completeForm.create_follow_up_ticket}
                        onChange={e => setCompleteForm(prev => ({ ...prev, create_follow_up_ticket: e.target.checked }))}
                      />
                      Create reactive ticket for follow-up
                    </label>
                  </div>
                )}

                {completeForm.create_follow_up_ticket && (
                  <div className="follow-up-fields">
                    <div className="form-group">
                      <label>Ticket Subject</label>
                      <input
                        type="text"
                        value={completeForm.follow_up_subject}
                        onChange={e => setCompleteForm(prev => ({ ...prev, follow_up_subject: e.target.value }))}
                        placeholder={`Follow-up: ${getTaskTypeName(task)} - ${task.asset_ref}`}
                      />
                    </div>
                    <div className="form-group">
                      <label>Fault Details *</label>
                      <textarea
                        value={completeForm.follow_up_description}
                        onChange={e => setCompleteForm(prev => ({ ...prev, follow_up_description: e.target.value }))}
                        rows={3}
                        placeholder="Describe the issue requiring follow-up..."
                        required={completeForm.create_follow_up_ticket}
                      />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Priority</label>
                        <select
                          value={completeForm.follow_up_priority}
                          onChange={e => setCompleteForm(prev => ({ ...prev, follow_up_priority: e.target.value }))}
                        >
                          <option value="low">Low</option>
                          <option value="normal">Normal</option>
                          <option value="high">High</option>
                          <option value="critical">Critical</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Fault Category</label>
                        <select
                          value={completeForm.follow_up_fault_category}
                          onChange={e => setCompleteForm(prev => ({ ...prev, follow_up_fault_category: e.target.value }))}
                        >
                          <option value="">Select...</option>
                          {faultCategories.map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowCompleteModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={saving}>
                  {saving ? 'Saving...' : 'Complete & Sign Off'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reschedule Task</h2>
              <button className="modal-close" onClick={() => setShowRescheduleModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleRescheduleSubmit}>
              <div className="modal-body">
                <p className="modal-info">
                  Current due date: <strong>{formatDate(task.current_due_date)}</strong>
                </p>
                <div className="form-group">
                  <label>New Due Date *</label>
                  <input
                    type="date"
                    value={rescheduleForm.new_due_date}
                    onChange={e => setRescheduleForm(prev => ({ ...prev, new_due_date: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Reason *</label>
                  <textarea
                    value={rescheduleForm.reason}
                    onChange={e => setRescheduleForm(prev => ({ ...prev, reason: e.target.value }))}
                    rows={3}
                    placeholder="Reason for rescheduling..."
                    required
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowRescheduleModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={saving}>
                  {saving ? 'Saving...' : 'Reschedule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default MaintenanceTaskDetail;
